#import "src/queries/fragments/personCardFragment.graphql"
#import "src/queries/fragments/personManagerFragment.graphql"
#import "./fragments/allocationProjectFragment.graphql"

#-------- FRAGMENTS --------
fragment BaseFragmentForGroup on Group {
  id
  isHidden
  name
  directParentId
  hierarchyIds
  parentIds
  type
  kind
}

fragment FragmentForGroup on Group {
  ...BaseFragmentForGroup
  hierarchy
  attributes {
    id
    attributeType
    subtype
    value
    label
  }
}

fragment MacroAllocationAbsoluteFragment on MacroAllocationAbsolute {
  id
  allocationProjectId
  requestorGroupId
  approverGroupId
  targetGroupId
  targetGroup {
    ...BaseFragmentForGroup
  }
  sourceGroupId
  sourceGroup {
    ...BaseFragmentForGroup
  }
  allocatedMemberFte
  requested
  realtimeCurrentMemberFte
  requestSubmitted
  approved
  approvalSubmitted
  requestStatus
  approvalStatus
  requestSavedAt
  approvalSavedAt
  requestSubmittedAt
  approvalSubmittedAt
  forecast
  forecastedAt
  note
  approvalReasonId
  approvalReasonCustomText
  type
}

fragment AllocationGroupFragment on AllocationGroup {
  id
  isHidden
  name
  hierarchyIds
  directParentId
  parentIds
  type
}

fragment AllocationGroupFragmentWithMemberCount on AllocationGroup {
  ...AllocationGroupFragment
  memberCount
}

# Includes hierarchy for tooltips (e.g. Requestor Team Builder source groups). Use default AllocationGroupFragment elsewhere.
fragment AllocationGroupFragmentWithHierarchy on AllocationGroup {
  ...AllocationGroupFragment
  hierarchy
}

fragment ForecastBudgetsFragment on AllocationGroup {
  budgets {
    allocationProjectId
    targetGroupId
    value
  }
  dailyRate
}

fragment FragmentForIndividualAllocation on IndividualAllocation {
  allocationProjectId
  allocationId
  targetGroupId
  targetGroup {
    ...FragmentForGroup
  }
  personId
  fte
  existingFte
  status
}

#-------- APPROVER PAGE MAIN QUERIES --------

query getAllocationProjects {
  allocationProjects: getAllocationProjects {
    ...AllocationProjectFragment
  }
}

query approverForecastMainQuery(
  $groupId: String!
  $supplyGroupTypes: [String]!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
  $enableFinancialForecasting: Boolean = false
  $parentIds: [String]
  $loadBudgetTargetGroups: Boolean = false
  $loadDemandParentGroups: Boolean = true
  $budgetTargetGroupIds: [String]
) {
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      memberCount
    }
    targetGroup {
      parentGroups @include(if: $loadDemandParentGroups) {
        ...BaseFragmentForGroup
        directParentId
      }
    }
  }
  descendantSupplyGroups: getDescendantGroups(
    groupId: $groupId
    groupTypes: $supplyGroupTypes
  ) {
    ...AllocationGroupFragmentWithMemberCount
    ...ForecastBudgetsFragment
  }
  relatedSupplyGroups: getGroups(groupIds: $parentIds)
    @include(if: $enableFinancialForecasting) {
    id
    dailyRate
  }
  relatedBudgetTargetGroups: getGroups(groupIds: $budgetTargetGroupIds)
    @include(if: $loadBudgetTargetGroups) {
    ...BaseFragmentForGroup
  }
}

query approverConstraintsMainQuery(
  $groupIdAsString: String!
  $groupIdAsId: ID!
  $supplyGroupTypes: [String]!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupIdAsString
  ) {
    id
    groupId
    group {
      ...FragmentForGroup
    }
    allocationProjectId
    directConstraint
    hierarchyConstraint
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupIdAsString
    perspective: $perspective
    enableTypedAllocations: false
  ) {
    ...MacroAllocationAbsoluteFragment
  }
  descendantSupplyGroups: getDescendantGroups(
    groupId: $groupIdAsString
    groupTypes: $supplyGroupTypes
  ) {
    ...AllocationGroupFragmentWithMemberCount
    totalCapacityFte
    directMembersCapacityFte
  }
  rootGroupTotalCapacityFte: getFteSum(
    groupId: $groupIdAsId
    fteSumType: CAPACITY_FTE
    fteSumTarget: ALL_MEMBERS
  )
  rootGroupDirectMembersCapacityFte: getFteSum(
    groupId: $groupIdAsId
    fteSumType: CAPACITY_FTE
    fteSumTarget: DIRECT_MEMBERS
  )
}

fragment CommentFragment on Comment {
  id
  threadId
  authorId
  authorTitle
  timestamp
  content
  contentMarkup
  tags
}

query approverPlannerMainQuery(
  $groupId: String!
  $supplyGroupTypes: [String]!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $enablePlannerBudgetTarget: Boolean = false
  $loadDemandParentGroups: Boolean = true
  $parentIds: [String]
  $referenceDate: Date
) {
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: false
    referenceDate: $referenceDate
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      memberCount
    }
    targetGroup {
      parentGroups @include(if: $loadDemandParentGroups) {
        ...BaseFragmentForGroup
        directParentId
      }
    }
  }
  descendantSupplyGroups: getDescendantGroups(
    groupId: $groupId
    groupTypes: $supplyGroupTypes
  ) {
    ...AllocationGroupFragmentWithMemberCount
    ...ForecastBudgetsFragment
  }
  relatedSupplyGroups: getGroups(groupIds: $parentIds)
    @include(if: $enablePlannerBudgetTarget) {
    id
    dailyRate
  }
}

query approverPlannerSecondQuery($commentFilter: [[String]]!, $pageSize: Int) {
  result: getCommentsV2(filters: $commentFilter, page: 0, pageSize: $pageSize) {
    comments {
      ...CommentFragment
    }
  }
}

query approverTeamBuilderMainQueryPart1(
  $allocationProjectId: String!
  $inDirectGroupIds: [String]
  $directGroupIds: [String]
) {
  result: getIntersectingTargetGroupsMembersAndStats(
    allocationProjectId: $allocationProjectId
    inDirectGroupIds: $inDirectGroupIds
    directGroupIds: $directGroupIds
  ) {
    members {
      aggregateId
      bands
      fte
      surname
      firstName
      preferredName
      address
      allocations {
        targetGroupId
        targetGroup {
          ...BaseFragmentForGroup
        }
        fte
      }
      directTeamIds
      memberOf {
        id
        name
        hierarchy
      }
    }
    groups {
      groupId
      group {
        ...FragmentForGroup
      }
      memberCount
      memberFte
      totalFte
      currentMemberCount
      currentMemberFte
    }
  }
}

query approverTeamBuilderMainQueryPart2(
  $allocationProjectId: String!
  $approverGroupId: String!
) {
  # getApproverMacroAllocationSummary is deprecated
  marcoAllocationSummary: getApproverMacroAllocationSummary(
    allocationProjectId: $allocationProjectId
    approverGroupId: $approverGroupId
    submittedValuesOnly: true
  ) {
    groupId
    group {
      ...FragmentForGroup
    }
    totalApproved
    totalRequested
  }
}

query approverTeamBuilderMainQueryPart3(
  $allocationProjectId: String!
  $approverGroupId: String!
) {
  individualAllocations: getIndividualAllocationsForGroup(
    allocationProjectId: $allocationProjectId
    groupIds: [$approverGroupId]
    queryType: ALLOCATIONS_FOR_MEMBERS_CURRENTLY_WITHIN_GROUP
    includeIndirect: true
  ) {
    ...FragmentForIndividualAllocation
  }
}

#-------- REQUESTOR PAGE MAIN QUERIES --------

query requestorForecastMainQuery(
  $groupId: String!
  $targetGroupTypes: [String]!
  $roleAreaTypes: [String]!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
  $enableFinancialForecasting: Boolean = false
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupId
  ) {
    id
    groupId
    allocationProjectId
    directConstraint
    hierarchyConstraint
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      memberCount
      dailyRate
    }
  }
  descendantTargetGroups: getDescendantGroups(
    groupId: $groupId
    groupTypes: $targetGroupTypes
  ) {
    ...AllocationGroupFragment
  }
  roleAreaGroups: getDescendantGroups(groupTypes: $roleAreaTypes) {
    ...AllocationGroupFragment
    ...ForecastBudgetsFragment @include(if: $enableFinancialForecasting)
  }
  relatedSupplyGroups: getRelatedBudgetGroups(groupId: $groupId)
    @include(if: $enableFinancialForecasting) {
    ...AllocationGroupFragment
    ...ForecastBudgetsFragment
  }
}

query requestorForecastMainQueryForGroup(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
) {
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
    currentGroupOnly: true
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      memberCount
      dailyRate
    }
  }
}

query requestorPlannerMainQuery(
  $groupId: String!
  $targetGroupTypes: [String]!
  $roleAreaTypes: [String]!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $enablePlannerBudgetTarget: Boolean = false
  $referenceDate: Date
) {
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: false
    referenceDate: $referenceDate
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      memberCount
      dailyRate
      attributes {
        id
        attributeType
        subtype
        label
        value
      }
    }
  }
  descendantTargetGroups: getDescendantGroups(
    groupId: $groupId
    groupTypes: $targetGroupTypes
  ) {
    ...AllocationGroupFragment
    objectiveIds
    objectiveVerifiedFor
    linkedStrategies {
      id
      datasource
    }
  }
  roleAreaGroups: getDescendantGroups(groupTypes: $roleAreaTypes) {
    ...AllocationGroupFragment
    ...ForecastBudgetsFragment @include(if: $enablePlannerBudgetTarget)
  }
  relatedSupplyGroups: getRelatedBudgetGroups(groupId: $groupId)
    @include(if: $enablePlannerBudgetTarget) {
    ...AllocationGroupFragment
    ...ForecastBudgetsFragment
  }
}

query lineOfBusinessPlannerMainQuery(
  $groupId: String!
  $lineOfBusinessGroupTypes: [String]!
) {
  descendantTargetGroups: getDescendantGroups(
    groupId: $groupId
    groupTypes: $lineOfBusinessGroupTypes
  ) {
    ...AllocationGroupFragmentWithMemberCount
    objectiveIds
    objectiveVerifiedFor
  }
}

query requestorTeamBuilderMainQuery(
  $requestorGroupId: String!
  $allocationProjectId: String!
  $inDirectGroupIds: [String]
  $directGroupIds: [String]
) {
  members: getIntersectingTargetGroupsMembersAndStats(
    allocationProjectId: $allocationProjectId
    inDirectGroupIds: $inDirectGroupIds
    directGroupIds: $directGroupIds
  ) {
    members {
      aggregateId
      sourceGroupIds
      bands
      fte
      surname
      firstName
      preferredName
      city
      address
      allocations {
        targetGroupId
        targetGroup {
          ...FragmentForGroup
        }
        fte
      }
    }
  }
  sourceGroupFtes: getIntersectingSourceGroupsFteStats(
    allocationProjectId: $allocationProjectId
    inDirectGroupIds: $inDirectGroupIds
    directGroupIds: $directGroupIds
  ) {
    groupId
    group {
      ...FragmentForGroup
    }
    memberFte
  }
  macroAllocationTargets: getMacroAllocationsAbsoluteValues(
    allocationProjectId: $allocationProjectId
    requestorGroupId: $requestorGroupId
    enableTypedAllocations: false
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      ...FragmentForGroup
    }
    targetGroup {
      ...FragmentForGroup
    }
  }
  individualAllocations: getIndividualAllocationsForGroup(
    allocationProjectId: $allocationProjectId
    groupIds: [$requestorGroupId]
    queryType: ALLOCATIONS_FOR_MEMBERS_ALLOCATED_WITHIN_GROUP
    includeIndirect: true
  ) {
    ...FragmentForIndividualAllocation
    person {
      aggregateId
      sourceGroupIds
      bands
      fte
      surname
      firstName
      preferredName
      address
      allocations {
        targetGroupId
        targetGroup {
          ...FragmentForGroup
        }
        fte
      }
    }
  }
}

#-------- OTHER UNCATEGORISED QUERIES --------

query searchGroupMembersQuery($input: SearchGroupMembersInput) {
  result: searchGroupMembers(input: $input) {
    members {
      aggregateId
      firstName
      surname
      preferredName
      bands
      jobTitle
      gender
      avatar
      fte
      sourceGroupIds
      disabled
      allocations {
        fte
        targetGroupId
      }
      tags: appliedTags {
        id
        tagId
        type
        displayValue
        isActive
      }
    }
    error {
      code
      message
    }
  }
}

query getIndividualAllocations(
  $allocationProjectId: String!
  $personId: String!
) {
  individualAllocations: getIndividualAllocations(
    allocationProjectId: $allocationProjectId
    personId: $personId
    status: ["allocated", "preallocated"]
  ) {
    allocationProjectId
    allocationId
    targetGroupId
    targetGroup {
      ...BaseFragmentForGroup
      hierarchy
    }
    personId
    fte
    existingFte
    status
  }
}

query getDescendantGroups($groupId: String, $groupTypes: [String]) {
  groups: getDescendantGroups(groupId: $groupId, groupTypes: $groupTypes) {
    ...AllocationGroupFragmentWithHierarchy
  }
}

query getGroupsByIds($groupIds: [String]) {
  sourceGroups: getGroups(groupIds: $groupIds) {
    ...FragmentForGroup
  }
}

query getRequestorIndividualAllocation(
  $allocationProjectId: String!
  $requestorGroupId: String!
  $inDirectGroupIds: [String]
  $directGroupIds: [String]
) {
  members: getIntersectingTargetGroupsMembersAndStats(
    allocationProjectId: $allocationProjectId
    inDirectGroupIds: $inDirectGroupIds
    directGroupIds: $directGroupIds
  ) {
    members {
      aggregateId
      sourceGroupIds
      bands
      fte
      surname
      firstName
      preferredName
      address
      allocations {
        targetGroupId
        targetGroup {
          ...FragmentForGroup
        }
        fte
      }
    }
  }

  individualAllocations: getIndividualAllocationsForGroup(
    allocationProjectId: $allocationProjectId
    groupIds: [$requestorGroupId]
    queryType: ALLOCATIONS_FOR_MEMBERS_ALLOCATED_WITHIN_GROUP
    includeIndirect: true
  ) {
    ...FragmentForIndividualAllocation
    person {
      aggregateId
      sourceGroupIds
      bands
      fte
      surname
      firstName
      preferredName
      address
      allocations {
        targetGroupId
        targetGroup {
          ...FragmentForGroup
        }
        fte
      }
    }
  }
}

query getForecastCsvExport($groupId: String!) {
  csvExport: getForecastCsvExport(groupId: $groupId) {
    content
  }
}

query getPlannerCsvExport($groupId: String!) {
  csvExport: getPlannerCsvExport(groupId: $groupId) {
    content
  }
}

query getGroupMacroAllocations(
  $allocationProjectIds: [String]
  $groupId: String!
) {
  results: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    targetGroupId: $groupId
  ) {
    id
    allocationProjectId
    targetGroupId
    sourceGroupId
    allocatedMemberFte
    requested
    note
  }
}

query getIntersectingFteSumForDateRanges(
  $input: IntersectingFteSumForDateRangesInput!
) {
  results: getIntersectingFteSumForDateRanges(input: $input) {
    id
    dateRangeId
    targetGroupId
    sourceGroupId
    fte
    members {
      id
      personId
      proratedFte
    }
  }
}

#-------- MUTATIONS --------

#-------- FORECAST MUTATIONS --------

mutation setMacroAllocationForecast(
  $id: String!
  $allocationProjectId: String!
  $sourceGroupId: String!
  $targetGroupId: String!
  $forecast: Float
  $type: String
) {
  result: setMacroAllocationForecast(
    id: $id
    allocationProjectId: $allocationProjectId
    sourceGroupId: $sourceGroupId
    targetGroupId: $targetGroupId
    forecast: $forecast
    type: $type
  ) {
    id
    forecast
    forecastedAt
  }
}

mutation setMacroAllocationNote(
  $id: String!
  $allocationProjectId: String!
  $sourceGroupId: String!
  $targetGroupId: String!
  $note: JSON
  $type: String
) {
  macroAllocation: setMacroAllocationNote(
    id: $id
    allocationProjectId: $allocationProjectId
    sourceGroupId: $sourceGroupId
    targetGroupId: $targetGroupId
    note: $note
    type: $type
  ) {
    ...MacroAllocationAbsoluteFragment
  }
}

#-------- CONSTRAINT MUTATIONS --------

mutation setAllocationDirectConstraint(
  $allocationProjectId: String!
  $groupId: String!
  $directConstraint: Float
) {
  result: setAllocationDirectConstraint(
    allocationProjectId: $allocationProjectId
    groupId: $groupId
    directConstraint: $directConstraint
  ) {
    id
  }
}

mutation setAllocationHierarchyConstraint(
  $allocationProjectId: String!
  $groupId: String!
  $hierarchyConstraint: Float
) {
  result: setAllocationHierarchyConstraint(
    allocationProjectId: $allocationProjectId
    groupId: $groupId
    hierarchyConstraint: $hierarchyConstraint
  ) {
    id
  }
}

mutation setAllocationHierarchyTarget(
  $allocationProjectId: String!
  $groupId: String!
  $hierarchyConstraint: Float
) {
  result: setAllocationHierarchyTarget(
    allocationProjectId: $allocationProjectId
    groupId: $groupId
    hierarchyConstraint: $hierarchyConstraint
  ) {
    id
  }
}

#-------- PLANNER MUTATIONS --------

mutation requestMacroAllocationAbsolute(
  $id: String!
  $allocationProjectId: String!
  $targetGroupId: String!
  $sourceGroupId: String!
  $requested: Float
  $realtimeCurrentMemberFte: Float
) {
  macroAllocation: requestMacroAllocationAbsolute(
    id: $id
    allocationProjectId: $allocationProjectId
    targetGroupId: $targetGroupId
    sourceGroupId: $sourceGroupId
    requested: $requested
    realtimeCurrentMemberFte: $realtimeCurrentMemberFte
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      ...FragmentForGroup
    }
    targetGroup {
      ...FragmentForGroup
    }
    requestorGroup {
      ...FragmentForGroup
    }
  }
}

mutation approveMacroAllocationAbsolute(
  $id: String!
  $allocationProjectId: String!
  $targetGroupId: String!
  $sourceGroupId: String!
  $approved: Float
  $approvalReasonId: String
  $approvalReasonCustomText: String
) {
  macroAllocation: approveMacroAllocationAbsolute(
    id: $id
    allocationProjectId: $allocationProjectId
    targetGroupId: $targetGroupId
    sourceGroupId: $sourceGroupId
    approved: $approved
    approvalReasonId: $approvalReasonId
    approvalReasonCustomText: $approvalReasonCustomText
  ) {
    ...MacroAllocationAbsoluteFragment
    sourceGroup {
      ...FragmentForGroup
    }
    targetGroup {
      ...FragmentForGroup
    }
  }
}

mutation submitMacroAllocationRequests(
  $allocationProjectId: String!
  $requestorGroupId: String!
) {
  macroAllocation: submitMacroAllocationRequests(
    allocationProjectId: $allocationProjectId
    requestorGroupId: $requestorGroupId
  )
}

mutation submitMacroAllocationApprovals(
  $allocationProjectId: String!
  $approverGroupId: String!
) {
  macroAllocation: submitMacroAllocationApprovals(
    allocationProjectId: $allocationProjectId
    approverGroupId: $approverGroupId
  )
}

#-------- TEAM-BUILDER MUTATIONS --------

mutation allocateIndividualAllocation(
  $allocationId: String!
  $allocationProjectId: String!
  $targetGroupId: String!
  $personId: String!
  $requestedFte: Float
) {
  individualAllocations: allocateIndividualAllocation(
    allocationId: $allocationId
    allocationProjectId: $allocationProjectId
    targetGroupId: $targetGroupId
    personId: $personId
    requestedFte: $requestedFte
  ) {
    ...FragmentForIndividualAllocation
  }
}

mutation unallocateIndividualAllocation(
  $allocationId: String!
  $allocationProjectId: String!
  $targetGroupId: String!
  $personId: String!
) {
  individualAllocations: unallocateIndividualAllocation(
    allocationId: $allocationId
    allocationProjectId: $allocationProjectId
    targetGroupId: $targetGroupId
    personId: $personId
  ) {
    ...FragmentForIndividualAllocation
  }
}

mutation updateFteIndividualAllocation(
  $allocationId: String!
  $allocationProjectId: String!
  $targetGroupId: String!
  $personId: String!
  $requestedFte: Float!
) {
  individualAllocations: updateFteIndividualAllocation(
    allocationId: $allocationId
    allocationProjectId: $allocationProjectId
    targetGroupId: $targetGroupId
    personId: $personId
    requestedFte: $requestedFte
  ) {
    ...FragmentForIndividualAllocation
  }
}

#-------- FORECAST MUTATIONS --------
mutation submitGroupForecasts($input: SubmitGroupForecastsInput!) {
  result: submitGroupForecasts(input: $input) {
    id
    forecastSubmittedFor
    forecastSubmittedAt
  }
}

mutation bulkMoveIndividuals($input: BulkMoveIndividualsInput!) {
  result: bulkMoveIndividuals(input: $input) {
    directGroups {
      id
      memberCount
      directMemberCount
      members(limit: 500) {
        ...PersonCardFragment
        ...PersonManagerFragment
        isPlaceholder
        isVacancy
      }
    }
    members {
      aggregateId
      memberOf {
        id
        parentIds
        name
        type
        hierarchy
        hierarchyIds
        memberCount
      }
    }
  }
}

# Org chart DnD real-time move: edits the live structure, no allocation project required.
# Success/error only; roster data comes from getTeamAndMembersAndChildTeamsQuery.
mutation bulkMoveIndividualsRealTimeOrgChart($input: MoveIndividualsInput!) {
  result: bulkMoveIndividualsRealTime(input: $input) {
    directGroups {
      id
    }
  }
}
